if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    getMeterSelector() {
      return document.querySelector('.meter-quantity-selector');
    }

    validateMeterAdd() {
      const meterSelector = this.getMeterSelector();
      if (!meterSelector || typeof meterSelector.meterCanAdd !== 'function') return { ok: true };
      return meterSelector.meterCanAdd();
    }

    metersFromShopifyQty(quantity, factor) {
      const f = parseInt(factor, 10) || 2;
      return (Math.round((quantity / f) * 2) / 2).toFixed(1);
    }

    formatDisplayMeters(value) {
      const n = Math.round(parseFloat(value) * 2) / 2;
      if (isNaN(n)) return '0';
      return Number.isInteger(n) ? String(n) : n.toFixed(1);
    }

    isMeterLineItem(item) {
      if (!item || !item.properties) return false;
      const props = item.properties;
      return props._sold_by_meter === 'true' || props['_sold_by_meter'] === 'true' || props.Meters != null;
    }

    isPartialInventoryAdd(response) {
      if (!response || !response.status) return false;
      const text = String(response.description || response.message || '').toLowerCase();
      return text.includes('were added') || text.includes('was added');
    }

    parseAddedQtyFromMessage(response) {
      const text = String(response.description || response.message || '');
      const match = text.match(/only\s+(\d+)\s+items?\s+were\s+added/i);
      if (!match) return null;
      return parseInt(match[1], 10);
    }

    meterPartialAddMessage(response, factor) {
      const addedQty = this.parseAddedQtyFromMessage(response);
      if (addedQty != null) {
        const meters = this.formatDisplayMeters(addedQty / (parseInt(factor, 10) || 2));
        return `Only ${meters} meters were added to your cart due to availability.`;
      }
      return response.description || response.message || 'Some meters could not be added due to availability.';
    }

    async fetchCartJson() {
      const cartUrl = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js';
      const res = await fetch(cartUrl, { credentials: 'same-origin' });
      return res.json();
    }

    async fetchCartSections() {
      if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') return null;
      const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${root}?sections=${encodeURIComponent(sectionIds.join(','))}`);
      return res.json();
    }

    async syncMeterLineProperties(item) {
      if (!this.isMeterLineItem(item)) return item;
      const props = Object.assign({}, item.properties || {});
      const factor = parseInt(props._meter_factor || props['_meter_factor'], 10) || 2;
      const metersStr = this.metersFromShopifyQty(item.quantity, factor);
      if (String(props.Meters) === metersStr) return item;

      props.Meters = metersStr;
      props._sold_by_meter = props._sold_by_meter || props['_sold_by_meter'] || 'true';
      props._meter_factor = String(factor);
      if (!props.Unit) props.Unit = 'Meter';

      try {
        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: props
          })
        });
        const cart = await response.json();
        if (cart && cart.items) {
          const updated =
            cart.items.find((line) => line.key === item.key) ||
            cart.items.find((line) => line.variant_id === item.variant_id);
          return updated || item;
        }
      } catch (e) {
        console.error(e);
      }
      return item;
    }

    async syncMeterLinesForVariant(variantId) {
      const cart = await this.fetchCartJson();
      const lines = (cart.items || []).filter(
        (item) => String(item.variant_id) === String(variantId) && this.isMeterLineItem(item)
      );
      for (const line of lines) {
        await this.syncMeterLineProperties(line);
      }
      return this.fetchCartJson();
    }

    notifyMeterSelector(variantId, cart, options = {}) {
      const meterSelector = this.getMeterSelector();
      if (!meterSelector) return;

      const cartQty = (cart.items || [])
        .filter((item) => String(item.variant_id) === String(variantId))
        .reduce((sum, item) => sum + item.quantity, 0);

      // Partial add: Shopify gave all remaining stock — clamp inventory to cart qty.
      // Normal add: only refresh cart qty so remaining meters stay correct.
      if (options.stockExhausted && typeof meterSelector.applyInventoryFromCart === 'function') {
        meterSelector.applyInventoryFromCart(variantId, cartQty);
      } else if (typeof meterSelector.refreshMeterCartQty === 'function') {
        meterSelector.refreshMeterCartQty();
      } else {
        document.dispatchEvent(new CustomEvent('meter:cart-updated'));
      }
    }

    async handleMeterPartialAdd(response, variantId) {
      const factor = 2;
      const message = this.meterPartialAddMessage(response, factor);

      // Shopify already added available stock — fix Meters to match real qty.
      const cart = await this.syncMeterLinesForVariant(variantId);

      let sections = null;
      try {
        sections = await this.fetchCartSections();
      } catch (e) { }

      if (this.cart && sections) {
        this.cart.renderContents({ sections: sections });
        if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
      }

      this.notifyMeterSelector(variantId, cart, { stockExhausted: true });
      this.handleErrorMessage(message);
      return cart;
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      const meterCheck = this.validateMeterAdd();
      if (!meterCheck.ok) {
        this.handleErrorMessage(meterCheck.message || 'Unable to add the selected meters to your cart.');
        return;
      }

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      const variantId = formData.get('id');
      const isMeterAdd = !!formData.get('properties[_sold_by_meter]') || !!formData.get('properties[Meters]');

      if (this.cart) {
        formData.append(
          'sections',
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      config.body = formData;

      const requestedQty = parseInt(formData.get('quantity'), 10) || 0;
      const requestedMeters = formData.get('properties[Meters]');

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then(async (response) => {
          // Shopify may return 422 AND still add available stock.
          if (response.status && isMeterAdd && this.isPartialInventoryAdd(response)) {
            await this.handleMeterPartialAdd(response, variantId);
            this.error = false;
            return;
          }

          if (response.status) {
            // Still refresh meter UI / cart if anything changed.
            if (isMeterAdd) {
              try {
                const cart = await this.fetchCartJson();
                this.notifyMeterSelector(variantId, cart);
              } catch (e) { }
            }

            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          // Keep Meters property aligned with actual Shopify qty.
          if (this.isMeterLineItem(response)) {
            const factor =
              parseInt(
                (response.properties &&
                  (response.properties._meter_factor || response.properties['_meter_factor'])) ||
                2,
                10
              ) || 2;
            if (
              response.quantity !== requestedQty ||
              (requestedMeters != null &&
                String(requestedMeters) !== this.metersFromShopifyQty(response.quantity, factor))
            ) {
              await this.syncMeterLineProperties(response);
              try {
                const sections = await this.fetchCartSections();
                if (sections) response.sections = sections;
              } catch (e) { }
            }
          }

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  this.cart.renderContents(response);
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }

          try {
            const cart = await this.fetchCartJson();
            this.notifyMeterSelector(variantId, cart);
          } catch (e) {
            document.dispatchEvent(new CustomEvent('meter:cart-updated'));
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper =
        this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage =
        this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}

$(document).ajaxComplete(function () {
  CartJS.updateCart();
});

